import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { Row, Col, Card, Button } from "reactstrap";
import fetchVariationChildService from "../../../../../Service/OPSServices/AmazonProductListing_v1/fetchVariationChildService";
import FetchAllAssetsAsinWiseService from "../../../../../Service/OPSServices/AmazonProductListing_v1/FetchAllAssetsAsinWiseService";
import TablePagination from "../../../../Components/Pagination/TablePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import bulkUpdateVariationService from "../../../../../Service/OPSServices/AmazonProductListing_v1/bulkUpdateVariationService";
import CustomModal from "../../../../Components/Modals/CustomModal";
import AssetsgalleryImges from "./AssetsGalleryImges";
import EditIcon from '../../../../../designUtils/OpsDashboard/AccountDetails/EditIcon.svg';
import DeleteIcon from '../../../../../../src/designUtils/ProjectDashboard/DeleteIcon.svg';
import DownloadIcon from '../../../../../designUtils/ProjectDashboard/DownloadIcon.svg';
import { faChevronRight, faChevronDown, faFilePdf, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

// Parallel image loader with concurrency limit — avoids sequential fetch slowness
function loadImagesWithLimit(urls, limit) {
    var cache = {};
    var queue = urls.slice();

    function loadOne(url) {
        return new Promise(function (resolve) {
            var img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = function () {
                cache[url] = img;
                resolve();
            };
            img.onerror = function () {
                cache[url] = null;
                resolve();
            };
            img.src = url + (url.indexOf("?") !== -1 ? "&" : "?") + "t=" + Date.now();
        });
    }

    function worker() {
        if (queue.length === 0) return Promise.resolve();
        var url = queue.shift();
        return loadOne(url).then(worker);
    }

    var workers = [];
    for (var i = 0; i < limit; i++) workers.push(worker());

    return Promise.all(workers).then(function () { return cache; });
}
function get(obj, path, fallback) {
    var result = obj;
    var parts = path.split(".");
    for (var i = 0; i < parts.length; i++) {
        if (result === null || result === undefined) return fallback;
        result = result[parts[i]];
    }
    return result === undefined || result === null ? fallback : result;
}

function deriveFieldMeta(field) {
    var rules = get(field, "schema.rules", {}) || {};
    var isArray = rules.field_type === "array";
    var maxUnique = Number(rules.maxUniqueItems || rules.maxItems || 1);
    var minUnique = Number(rules.minUniqueItems || rules.minItems || 1);
    var isMulti = isArray && maxUnique > 1;   // bullets, backend keywords, …

    // maxLength: check nested_schema paths first, fall back to rules
    var maxLength =
        get(field, "schema.nested_schema.items.properties.value.maxLength", null) ||
        get(field, "schema.nested_schema.properties.value.maxLength", null) ||
        (rules.type === "string" ? rules.maxLength || null : null);

    var maxUtf8 =
        get(field, "schema.nested_schema.items.properties.value.maxUtf8ByteLength", null) ||
        get(field, "schema.nested_schema.properties.value.maxUtf8ByteLength", null) ||
        get(field, "schema.rules.maxUtf8ByteLength", null);

    maxLength = maxLength !== null ? Number(maxLength) : null;
    maxUtf8 = maxUtf8 !== null ? Number(maxUtf8) : null;

    return { isArray, isMulti, maxUnique, minUnique, maxLength, maxUtf8 };
}

/**
 * Detects whether this field represents an image attribute
 * (main_product_image_locator, other_product_image_locator_1, swatch_image, etc.)
 * so we can render a thumbnail + "Edit" (gallery) UI instead of a plain textarea.
 */
function isImageAttributeField(field) {
    var component = String(get(field, "render.component", "") || "").toUpperCase();
    if (component.indexOf("IMAGE") !== -1) return true;

    var fieldKey = String(get(field, "field", "") || "").toLowerCase();
    var format = get(field, "schema.rules.format", "") || get(field, "schema.format", "");
    if (fieldKey.indexOf("image") !== -1 && (format === "uri" || !format)) return true;

    return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Value helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Amazon SP-API returns attribute values as:
 *   [{value: "text1", marketplace_id: "..."}, {value: "text2", ...}]
 * OR sometimes as plain strings. Normalise to a string[].
 */
function extractStringArray(raw) {
    if (!raw) return [];
    var arr = Array.isArray(raw) ? raw : [raw];
    return arr.map(function (item) {
        if (item === null || item === undefined) return "";
        if (typeof item === "string") return item;
        if (typeof item === "object" && item.value !== undefined)
            return item.value !== null && item.value !== undefined ? String(item.value) : "";
        return String(item);
    });
}
function stripHtmlTags(text) {
    if (!text) return text;
    return String(text)
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<p[^>]*>/gi, "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .trim();
}

/** Flatten raw value to a single string (for single-value fields, incl. image URLs). */
function flattenSingle(raw) {
    if (raw === null || raw === undefined) return "";
    if (typeof raw === "string") return stripHtmlTags(raw);
    if (typeof raw === "number") return String(raw);
    var arr = extractStringArray(raw);
    return stripHtmlTags(arr.join("\n"));
}
function softWrapLongWords(text, maxWordLen) {
    maxWordLen = maxWordLen || 40;
    if (!text) return text;
    return String(text).split(/(\s+)/).map(function (chunk) {
        if (/\s/.test(chunk) || chunk.length <= maxWordLen) return chunk;
        var out = [];
        for (var i = 0; i < chunk.length; i += maxWordLen) {
            out.push(chunk.slice(i, i + maxWordLen));
        }
        return out.join(" ");
    }).join("");
}
// ─────────────────────────────────────────────────────────────────────────────
// AutoResizeTextarea
// ─────────────────────────────────────────────────────────────────────────────

function AutoResizeTextarea({ value, onChange, maxLength, placeholder }) {
    var ref = useRef(null);
    useEffect(function () {
        if (ref.current) {
            ref.current.style.height = "auto";
            ref.current.style.height = ref.current.scrollHeight + "px";
        }
    }, [value]);
    return (
        <textarea
            ref={ref}
            value={value}
            onChange={onChange}
            placeholder={placeholder || ""}
            rows={2}
            style={{
                width: "100%", resize: "none", overflow: "hidden",
                fontSize: 12, border: "1px solid #d1d5db", borderRadius: 5,
                padding: "6px 8px", lineHeight: 1.5, outline: "none", boxSizing: "border-box",
            }}
        />
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CharCounter  (handles both maxLength chars and maxUtf8 bytes)
// ─────────────────────────────────────────────────────────────────────────────

function CharCounter({ value, maxLength, maxUtf8 }) {
    if (!maxLength && !maxUtf8) return null;
    var text = typeof value === "string" ? value : "";
    var len, limit, label, over;

    if (maxLength) {
        len = text.length;
        limit = maxLength;
        label = limit;
        over = len > limit;
    } else {
        try { len = new TextEncoder().encode(text).length; }
        catch (e) { len = 0; }
        limit = maxUtf8;
        label = limit + " bytes";
        over = len > limit;
    }

    return (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
            <small style={{ fontSize: 10, color: over ? "#dc2626" : "#9ca3af" }}>
                {len} / {label}{over ? " — exceeds limit" : ""}
            </small>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// SingleRow  — one textarea + its counter (used for each bullet / item)
// ─────────────────────────────────────────────────────────────────────────────

function SingleRow({ label, value, onChange, maxLength, maxUtf8, placeholder }) {
    return (
        <div>
            {label && (
                <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 3 }}>{label}</div>
            )}
            <AutoResizeTextarea
                value={value}
                onChange={function (e) { onChange(e.target.value); }}
                placeholder={placeholder || ""}
            />
            <CharCounter value={value} maxLength={maxLength} maxUtf8={maxUtf8} />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// ImageFieldRow  — thumbnail + Edit button (opens the gallery popup)
// ─────────────────────────────────────────────────────────────────────────────

function ImageFieldRow({ value, fieldLabel, onEditClick }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
                width: 90, height: 90, border: "1px solid #e5e7eb", borderRadius: 6,
                overflow: "hidden", background: "#fff", display: "flex",
                alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {value ? (
                    <img src={value} alt={fieldLabel} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>No image</span>
                )}
            </div>
            <button
                type="button"
                onClick={function (e) { e.stopPropagation(); onEditClick(); }}
                title="Edit"
                style={{
                    background: "#fff", border: "1px solid #ffffff", borderRadius: 6,
                    width: 40, height: 40, display: "flex",
                    alignItems: "center", justifyContent: "center", cursor: "pointer",
                }}
            >
                <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
            </button>
        </div>
    );
}
function ConfirmUpdateModal({ editedRows, varAttrs, onConfirm, onCancel, submitting }) {
    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: 10, width: 800, maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #e5e7eb" }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#111" }}>
                        Confirm Update — {editedRows.length} product{editedRows.length > 1 ? "s" : ""}
                    </span>
                    <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#6b7280", lineHeight: 1 }}>×</button>
                </div>
                <div style={{ overflowY: "auto", flex: 1, padding: "12px 18px" }}>
                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
                        The following child products will be updated. Once confirmed, changes will be patched to Amazon.
                    </p>
                    {editedRows.map((row) => (
                        <div key={row.asin + "__" + (row.sku || "nosku")} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginBottom: 8, background: "#f9fafb" }}>
                            <div style={{ marginBottom: 6 }}>
                                <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#1a6bb5" }}>
                                    {row.asin}
                                </span>
                                {row.sku && (
                                    <span style={{ marginLeft: 8, fontFamily: "monospace", fontSize: 11, background: "#f3f4f6", border: "1px solid #dde1e7", borderRadius: 4, padding: "1px 6px", color: "#374151" }}>
                                        {row.sku}
                                    </span>
                                )}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {varAttrs.map((attr) => {
                                    var val = row.editedAttrs[attr];
                                    if (val === undefined || val === null) return null;
                                    return (
                                        <span key={attr} style={{ fontSize: 11, background: "#e8f4fd", border: "1px solid #b3d7f5", borderRadius: 4, padding: "2px 8px", color: "#1a6bb5" }}>
                                            <b>{attr}:</b> {String(val) || <i style={{ color: "#999" }}>empty</i>}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end" }}>
                    <Button className="no-btn-background-color mr-3" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button className="btn-background-color" 
                    // onClick={onConfirm} 
                    >
                        {submitting ? "Updating..." : "Update Listing"}
                    </Button>
                </div>
            </div>
            <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// ChildCard  — fully dynamic, no field names hardcoded
// ─────────────────────────────────────────────────────────────────────────────

function ChildCard({ child, fieldKey, fieldLabel, fieldMeta, isImgField, editedValues, onEdit, onImageEditClick }) {
    var baseKey = (child.sku || "nosku") + "__" + child.asin;
    var rawVal = child.attributes && child.attributes[fieldKey];
    var isMulti = fieldMeta.isMulti;
    var maxLength = fieldMeta.maxLength;
    var maxUtf8 = fieldMeta.maxUtf8;
    var [isOpen, setIsOpen] = useState(false);
    var dirty, content;

    if (isImgField) {
        // ── Image field — thumbnail + Edit (opens asset gallery popup) ──────────
        var origImgVal = flattenSingle(rawVal);
        var curImgVal = editedValues[baseKey] !== undefined ? editedValues[baseKey] : origImgVal;
        dirty = curImgVal !== origImgVal;

        content = (
            <div style={{ padding: "10px 12px" }}>
                <ImageFieldRow
                    value={curImgVal}
                    fieldLabel={fieldLabel}
                    onEditClick={function () { onImageEditClick(child, baseKey); }}
                />
            </div>
        );

    } else if (isMulti) {
        // ── Multi-item (bullet_point, generic_keyword, etc.) ────────────────────
        var origItems = extractStringArray(rawVal);
        if (origItems.length === 0) origItems = [""];

        var currentItems = editedValues[baseKey] !== undefined ? editedValues[baseKey] : origItems;
        dirty = JSON.stringify(currentItems) !== JSON.stringify(origItems);

        var filledItems = currentItems.filter(function (v) { return v !== "" && v !== null && v !== undefined; });
        var hasDuplicates = new Set(filledItems.map(function (v) { return String(v).trim().toLowerCase(); })).size !== filledItems.length;

        var minUnique = fieldMeta.minUnique || 1;
        var maxUnique = fieldMeta.maxUnique || 99;

        content = (
            <div style={{ padding: "10px 12px" }}>
                {currentItems.map(function (itemVal, i) {
                    return (
                        <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 8 }}>
                            <div style={{ flex: 1 }}>
                                <SingleRow
                                    label={fieldLabel + " " + (i + 1)}
                                    value={itemVal}
                                    onChange={function (v) {
                                        var updated = currentItems.slice();
                                        updated[i] = v;
                                        onEdit(baseKey, updated);
                                    }}
                                    maxLength={maxLength}
                                    maxUtf8={maxUtf8}
                                    placeholder={"Enter " + fieldLabel + " " + (i + 1) + "…"}
                                />
                            </div>
                            {currentItems.length > minUnique && (
                                <img
                                    src={DeleteIcon}
                                    alt="Delete"
                                    height={20}
                                    width={20}
                                    style={{
                                        display: "inline-block",
                                        width: "20px",
                                        height: "20px",
                                        marginTop: 18,
                                        backgroundColor: "#e72424",
                                        maskImage: `url(${DeleteIcon})`,
                                        WebkitMaskImage: `url(${DeleteIcon})`,
                                        maskRepeat: "no-repeat",
                                        WebkitMaskRepeat: "no-repeat",
                                        maskPosition: "center",
                                        WebkitMaskPosition: "center",
                                        maskSize: "contain",
                                        WebkitMaskSize: "contain",
                                        cursor: "pointer",
                                        flexShrink: 0,
                                    }}
                                    title="Delete"
                                    onClick={function () {
                                        var updated = currentItems.filter(function (_, idx) { return idx !== i; });
                                        onEdit(baseKey, updated);
                                    }}
                                />
                            )}
                        </div>
                    );
                })}

                {currentItems.length < maxUnique && (
                    <span
                        style={{ color: "#007185", cursor: "pointer", fontSize: 12 }}
                        onClick={function () { onEdit(baseKey, currentItems.concat([""])); }}
                    >
                        + Add More
                    </span>
                )}

                {hasDuplicates && (
                    <div style={{ color: "#dc2626", fontSize: 11, marginTop: 6 }}>
                        Duplicate values are not allowed
                    </div>
                )}
            </div>
        );
    } else {
        // ── Single-value field (item_name, product_description, etc.) ───────────
        var origVal = flattenSingle(rawVal);
        var curVal = editedValues[baseKey] !== undefined ? editedValues[baseKey] : origVal;
        dirty = curVal !== origVal;

        content = (
            <div style={{ padding: "10px 12px" }}>
                <SingleRow
                    value={curVal}
                    onChange={function (v) { onEdit(baseKey, v); }}
                    maxLength={maxLength}
                    maxUtf8={maxUtf8}
                    placeholder={"Enter " + fieldLabel + "…"}
                />
            </div>
        );
    }

    return (
        <div style={{
            border: dirty ? "1.5px solid #0073bb" : "1px solid #e5e7eb",
            borderRadius: 8, marginBottom: 10,
            background: "#fff",
            overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
            {/* Card header */}
            <Row className="m-0 align-items-center"
                style={{ padding: "7px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", cursor: "pointer" }}
                onClick={() => setIsOpen(prev => !prev)}>
                <Col xs="auto" className="ps-3 mr-2 p-0">
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>
                        ASIN:&nbsp;
                        <span style={{ fontFamily: "monospace", color: "#0073bb" }}>
                            {child.asin || "-"}
                        </span>
                    </span>
                </Col>
                <Col xs="auto" className="p-0">
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>
                        SKU:&nbsp;
                        <span style={{ fontFamily: "monospace", color: "#0073bb" }}>
                            {child.sku || "-"}
                        </span>
                    </span>
                </Col>
                <Col className="p-0 d-flex justify-content-end align-items-center">
                    {dirty && (
                        <span style={{
                            fontSize: 10, background: "#dbeafe", color: "#1d4ed8",
                            borderRadius: 4, padding: "2px 7px", fontWeight: 600,
                        }}>Edited</span>

                    )}
                    <FontAwesomeIcon
                        icon={isOpen ? faChevronDown : faChevronRight}
                        style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}
                    />
                </Col>
            </Row>

            {isOpen && content}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// VariationChildFieldPopup
// ─────────────────────────────────────────────────────────────────────────────

function VariationChildFieldPopup({ onClose, field, selectedAsin, sku, token, storeName ,integerId}) {
    var [loading, setLoading] = useState(false);
    var [error, setError] = useState(null);
    var [children, setChildren] = useState([]);
    var [editedValues, setEditedValues] = useState({});
    var [submitting, setSubmitting] = useState(false);
    var [showConfirm, setShowConfirm] = useState(false);
    var [successMsg, setSuccessMsg] = useState("");
    var [searchText, setSearchText] = useState("");
    var [currentPage, setCurrentPage] = useState(0);
    var [pageSize, setPageSize] = useState(8);
    var [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    var downloadMenuRef = useRef(null);

    useEffect(function () {
        function handleOutsideClick(e) {
            if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
                setDownloadMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleOutsideClick);
        return function () { document.removeEventListener("mousedown", handleOutsideClick); };
    }, []);

    var [imagePopup, setImagePopup] = useState({ open: false, child: null, baseKey: null, originalValue: null });
    var [galleryImg, setGalleryImg] = useState([]);
    var [galleryLoading, setGalleryLoading] = useState(false);
    var [selectedAsset, setSelectedAsset] = useState(null);

    var fieldKey = field && field.field;
    var fieldLabel = (field && field.label) || (fieldKey || "").replace(/_/g, " ");
    var buildFileName = function (ext) {
        var sanitize = function (str) {
            return String(str).trim().replace(/[^a-zA-Z0-9-_]+/g, "_");
        };
        var safeStore = storeName ? sanitize(storeName) : "";
        var safeAsin = selectedAsin ? sanitize(selectedAsin) : "";
        var safeSku = sku ? sanitize(sku) : "";
        var safeField = fieldLabel ? sanitize(fieldLabel.replace(/\s+/g, "_")) : "";
        var parts = [safeStore, safeAsin, safeSku, safeField].filter(Boolean);
        var base = parts.length > 0 ? parts.join("_") : "Data";
        return base + "_Variation_Family." + ext;
    };
    // ─────────────────────────────────────────────────────────

    // All schema-derived values come from deriveFieldMeta — nothing hardcoded
    var fieldMeta = deriveFieldMeta(field || {});
    var isImgField = isImageAttributeField(field || {});

    useEffect(function () {
        if (!selectedAsin || !fieldKey) return;
        setLoading(true);
        setError(null);
        setChildren([]);
        setEditedValues({});
        setSuccessMsg("");
        setSearchText("");
        setCurrentPage(0);

        fetchVariationChildService(token, {
            integerId: integerId,
            asin: selectedAsin,
            sku: sku || "",
            requested_fields: [fieldKey],
        }).then(function (res) {
            if (res && res.status) {
                setChildren((res.children || []).filter(function (c) { return !c.is_parent; }));
            } else {
                setError((res && res.message) || "Failed to load variation family data.");
            }
            setLoading(false);
        }).catch(function () {
            setError("Network error — could not load child data.");
            setLoading(false);
        });
    }, [selectedAsin, fieldKey]);

    var handleEdit = function (key, newValue) {
        setEditedValues(function (prev) {
            var next = Object.assign({}, prev);
            next[key] = newValue;
            return next;
        });
        setSuccessMsg("");
    };

    var handleClose = function () {
        setEditedValues({});
        setSuccessMsg("");
        setError(null);
        onClose();
    };

    var handleSearchChange = function (e) {
        setSearchText(e.target.value);
        setCurrentPage(0);
    };

    var handlePageChange = function (ref) {
        setCurrentPage(ref.currentPage);
        setPageSize(ref.pageSize);
    };
    // Common: get the current display value (edited if present, else original) for a child
    var getRowValue = function (child) {
        if (!child) return "";
        var baseKey = (child.sku || "nosku") + "__" + child.asin;
        var rawVal = child.attributes && child.attributes[fieldKey];
        if (fieldMeta.isMulti) {
            var orig = extractStringArray(rawVal);
            if (orig.length === 0) orig = [""];
            var cur = editedValues[baseKey] !== undefined ? editedValues[baseKey] : orig;
            return cur.filter(Boolean).join("; ");
        }
        return editedValues[baseKey] !== undefined ? editedValues[baseKey] : flattenSingle(rawVal);
    };

    var handleDownloadExcel = function () {
        var rows = [["ASIN", "SKU", fieldLabel]];
        filteredChildren.forEach(function (child) {
            rows.push([
                child.asin || "",
                child.sku || "",
                isImgField ? getRowValue(child) : getRowValueMultiline(child),
            ]);
        });

        var ws = XLSX.utils.aoa_to_sheet(rows);
        ws["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: isImgField ? 60 : 90 }];

        if (isImgField) {
            for (var i = 0; i < filteredChildren.length; i++) {
                var url = getRowValue(filteredChildren[i]);
                if (url) {
                    var cellRef = XLSX.utils.encode_cell({ r: i + 1, c: 2 });
                    if (ws[cellRef]) {
                        ws[cellRef].l = { Target: url, Tooltip: "Click to view image" };
                    }
                }
            }
        }

        var wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Variation Family");
        XLSX.writeFile(wb, buildFileName("xlsx"));
        setDownloadMenuOpen(false);
    };
    // Multiline (bullet-per-line) value — used only for the PDF export
    var getRowValueMultiline = function (child) {
        if (!child) return "-";
        var baseKey = (child.sku || "nosku") + "__" + child.asin;
        var rawVal = child.attributes && child.attributes[fieldKey];
        if (fieldMeta.isMulti) {
            var orig = extractStringArray(rawVal);
            if (orig.length === 0) orig = [""];
            var cur = editedValues[baseKey] !== undefined ? editedValues[baseKey] : orig;
            var filled = cur.filter(function (v) { return v !== null && v !== undefined && String(v).trim() !== ""; });
            if (filled.length === 0) return "-";
            return filled
                .map(function (v, i) {
                    return (i + 1) + ". " + softWrapLongWords(v, 35);
                })
                .join("\n\n");
        }
        var val = editedValues[baseKey] !== undefined ? editedValues[baseKey] : flattenSingle(rawVal);
        return val && String(val).trim() !== "" ? String(val) : "-";
    };
    var handleDownloadPDF = async function () {
        var doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        doc.setProperties({ title: (fieldLabel || "data") + " - Variation Family" });

        var pageWidth = doc.internal.pageSize.getWidth();
        var pageHeight = doc.internal.pageSize.getHeight();
        var marginX = 40;
        var marginBottom = 40;

        doc.setFontSize(13);
        doc.setTextColor(0, 0, 0);
        doc.text(fieldLabel + " — Variation Family (" + filteredChildren.length + " products)", marginX, 30);

        if (isImgField) {
            // ── Image field: table layout, images loaded in parallel (max 5 concurrent) ──
            var urls = filteredChildren
                .map(function (c) { return getRowValue(c); })
                .filter(Boolean);
            var uniqueUrls = urls.filter(function (u, i) { return urls.indexOf(u) === i; });

            var imageCache = await loadImagesWithLimit(uniqueUrls, 5);

            var body = filteredChildren.map(function (child) {
                return [child.asin || "-", child.sku || "-", ""];
            });

            var tableStartX = marginX;
            var tableWidth = pageWidth - 2 * tableStartX;

            doc.autoTable({
                head: [["ASIN", "SKU", fieldLabel]],
                body: body,
                startY: 55,
                margin: { left: tableStartX },
                styles: {
                    overflow: "linebreak",
                    fontSize: 8,
                    cellPadding: 8,
                    valign: "top",
                    cellWidth: "wrap",
                },
                theme: "grid",
                pageBreak: "auto",
                rowPageBreak: "avoid",
                headStyles: { fillColor: [26, 107, 181], textColor: 255, fontStyle: "bold" },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 200 },
                    2: { cellWidth: tableWidth - 100 - 200, minCellHeight: 58 },
                },
                didDrawCell: function (data) {
                    if (data.column.index !== 2 || data.row.section !== "body") return;
                    var child = filteredChildren[data.row.index];
                    var url = getRowValue(child);
                    var imgEl = url ? imageCache[url] : null;
                    if (!imgEl) return;
                    try {
                        doc.addImage(imgEl, "JPEG", data.cell.x + 4, data.cell.y + 4, 50, 50);
                    } catch (e) {
                        // skip broken image
                    }
                },
            });

            doc.save(buildFileName("pdf"));
            setDownloadMenuOpen(false);
            return;
        }

        // ── Long text field: card layout, no table (avoids row overlap) ──────────
        var y = 55;
        var labelColWidth = 130;
        var textColX = marginX + labelColWidth + 10;
        var textColWidth = pageWidth - textColX - marginX;
        var lineHeight = 12;
        var skuLineHeight = 10;

        doc.setFontSize(9);

        filteredChildren.forEach(function (child) {
            var text = getRowValueMultiline(child);
            var lines = doc.splitTextToSize(text, textColWidth);

            // ── FIX: wrap SKU within the label column width instead of a single overflowing line ──
            doc.setFont(undefined, "normal");
            doc.setFontSize(9);
            var skuLines = doc.splitTextToSize(child.sku || "-", labelColWidth - 5);

            // Block height must account for BOTH the bullet text height AND the wrapped SKU height
            var textBlockHeight = lines.length * lineHeight;
            var skuBlockHeight = 26 + (skuLines.length * skuLineHeight);
            var blockHeight = Math.max(textBlockHeight, skuBlockHeight, 40) + 16;

            if (y + blockHeight > pageHeight - marginBottom) {
                doc.addPage();
                y = 40;
            }

            doc.setFont(undefined, "bold");
            doc.setFontSize(9);
            doc.setTextColor(26, 107, 181);
            doc.text(child.asin || "-", marginX, y + 12);

            doc.setFont(undefined, "normal");
            doc.setFontSize(8);
            doc.setTextColor(90, 90, 90);
            doc.text(skuLines, marginX, y + 26);

            doc.setFont(undefined, "normal");
            doc.setFontSize(9);
            doc.setTextColor(30, 30, 30);
            doc.text(lines, textColX, y + 12);

            doc.setDrawColor(225, 225, 225);
            doc.line(marginX, y + blockHeight - 6, pageWidth - marginX, y + blockHeight - 6);

            y += blockHeight;
        });

        doc.save(buildFileName("pdf"));
        setDownloadMenuOpen(false);
    };

    // ── Image gallery popup handlers ────────────────────────────────────────────
    var handleImageEditClick = function (child, baseKey) {
        var rawVal = child.attributes && child.attributes[fieldKey];
        var originalVal = flattenSingle(rawVal);
        var currentVal = editedValues[baseKey] !== undefined ? editedValues[baseKey] : originalVal;

        setImagePopup({ open: true, child: child, baseKey: baseKey, originalValue: originalVal });
        setSelectedAsset(null);
        setGalleryImg([]);
        setGalleryLoading(true);

        FetchAllAssetsAsinWiseService(token, { asin: child.asin }).then(function (res) {
            var list = (res && res.fileList) || [];
            setGalleryImg(list);
            var matched = list.find(function (img) { return img.filePath === currentVal; });
            setSelectedAsset(matched || null);
            setGalleryLoading(false);
        }).catch(function () {
            setGalleryLoading(false);
        });
    };

    var closeImagePopup = function () {
        setImagePopup({ open: false, child: null, baseKey: null, originalValue: null });
        setSelectedAsset(null);
        setGalleryImg([]);
    };

    // "Replace" button inside AssetsgalleryImges
    var handleImageReplace = function () {
        if (!imagePopup.baseKey || !selectedAsset) return;
        handleEdit(imagePopup.baseKey, selectedAsset.filePath);
        closeImagePopup();
    };

    // Clicking the already-selected thumbnail again (toggle off) → revert to original value
    var handleImageUnselect = function () {
        if (!imagePopup.baseKey) return;
        handleEdit(imagePopup.baseKey, imagePopup.originalValue || "");
    };

    // Filter by SKU or ASIN
    var filteredChildren = children.filter(function (child) {
        if (!searchText.trim()) return true;
        var q = searchText.toLowerCase();
        return (child.sku || "").toLowerCase().includes(q) ||
            (child.asin || "").toLowerCase().includes(q);
    });

    var startIndex = currentPage * pageSize;
    var paginatedChildren = filteredChildren.slice(startIndex, startIndex + pageSize);

    // Build editedRows for submit — re-assemble array for multi-item fields
    var editedRows = children.map(function (child) {
        var baseKey = (child.sku || "nosku") + "__" + child.asin;
        var rawVal = child.attributes && child.attributes[fieldKey];

        if (fieldMeta.isMulti) {
            var origItems = extractStringArray(rawVal);
            if (origItems.length === 0) origItems = [""];
            var editedItems = editedValues[baseKey] !== undefined ? editedValues[baseKey] : origItems;
            var changed = JSON.stringify(editedItems) !== JSON.stringify(origItems);
            if (!changed) return null;
            var cleanedItems = editedItems.filter(function (v) { return v !== null && v !== undefined && String(v).trim() !== ""; });
            return { asin: child.asin, sku: child.sku, value: cleanedItems };
        }

        if (editedValues[baseKey] === undefined) return null;
        var origVal = flattenSingle(rawVal);
        if (editedValues[baseKey] === origVal) return null;
        return { asin: child.asin, sku: child.sku, value: editedValues[baseKey] };
    }).filter(Boolean);

    var hasEdits = editedRows.length > 0;

    var hasBlankEdits = editedRows.some(function (row) {
        if (Array.isArray(row.value)) {
            var nonEmpty = row.value.filter(function (v) {
                return v !== null && v !== undefined && String(v).trim() !== "";
            });
            return nonEmpty.length === 0;
        }
        return row.value === null || row.value === undefined || String(row.value).trim() === "";
    });
    var handleSubmit = function () {
        if (!hasEdits || submitting || hasBlankEdits) return;
        setShowConfirm(true);
    };
    var handleConfirmUpdate = async function () {
        setSubmitting(true);
        setError(null);

        var payload = editedRows.map(function (row) {
            return {
                integerId: integerId,
                asin: row.asin,
                skus: row.sku ? [row.sku] : [],
                attributes: { [fieldKey]: row.value },
                selectedAsin: selectedAsin,
                selectedSku: sku || "",
            };
        });

        try {
            var res = await bulkUpdateVariationService(token, payload);
            if (!res || !res.status) {
                setError((res && res.message) || "Update failed.");
                setShowConfirm(false);
                setSubmitting(false);
                return;
            }
            setChildren(function (prev) {
                return prev.map(function (child) {
                    var edited = editedRows.find(function (r) {
                        return r.asin === child.asin && r.sku === child.sku;
                    });
                    if (!edited) return child;
                    return Object.assign({}, child, {
                        attributes: Object.assign({}, child.attributes, { [fieldKey]: edited.value }),
                    });
                });
            });
            setEditedValues({});
            setShowConfirm(false);
            setSuccessMsg("Feed submitted for " + payload.length + " product" + (payload.length > 1 ? "s" : "") + ". Amazon is processing the update.");
            setTimeout(function () { setSuccessMsg(""); }, 8000);
        } catch (e) {
            setError("Update failed. Please try again.");
            setShowConfirm(false);
        }
        setSubmitting(false);
    };
    return (
        <>
            <Card className="p-3">
                {/* Search + Pagination */}
                <Row className="m-0 mb-3 align-items-center">
                    {/* Left side */}
                    <Col className="p-0">
                        <span style={{ fontSize: 21, fontWeight: 500, color: "#374151" }}>
                            Variation Family ({children.length})
                        </span>
                    </Col>
                    <Col xs="auto" className="p-0 mr-2">
                        <div style={{ position: "relative" }} ref={downloadMenuRef}>
                            <button
                                type="button"
                                onClick={function () { setDownloadMenuOpen(function (prev) { return !prev; }); }}
                                title="Download"
                                className="info-button-background-none"
                                disabled={loading || children.length === 0}
                            >
                                <img src={DownloadIcon} alt="Download" style={{ width: 25, height: 25 }} />
                            </button>


                            {downloadMenuOpen && (
                                <div style={{
                                    position: "absolute", top: "110%", left: 0, background: "#fff",
                                    border: "1px solid #e5e7eb", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                    zIndex: 999, minWidth: 71, overflow: "hidden",
                                }}>
                                    <div
                                        onClick={handleDownloadExcel}
                                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer", color: "#166534" }}
                                        onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
                                        onMouseLeave={function (e) { e.currentTarget.style.background = "#fff"; }}
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} />
                                        Excel
                                    </div>
                                    <div
                                        onClick={handleDownloadPDF}
                                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer", color: "#b91c1c", borderTop: "1px solid #f3f4f6" }}
                                        onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
                                        onMouseLeave={function (e) { e.currentTarget.style.background = "#fff"; }}
                                    >
                                        <FontAwesomeIcon icon={faFilePdf} />
                                        PDF
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Right side */}
                    <Col xs="auto" className="p-0 mr-3">
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder="Search by SKU or ASIN..."
                            style={{
                                width: "25vw",
                                padding: "8px 12px",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                fontSize: 13,
                                outline: "none",
                                boxSizing: "border-box",
                            }}
                        />
                    </Col>

                    <Col xs="auto" className="p-0">
                        <TablePagination
                            pageSize={pageSize}
                            totalRecords={filteredChildren.length}
                            onPageChange={handlePageChange}
                            currentPage={currentPage}
                        />
                    </Col>
                </Row>

                {/* Body */}
                <div style={{ minHeight: 120, maxHeight: "55vh", overflowY: "auto", paddingRight: 2 }}>

                    {loading && (
                        <Row className="m-0">
                            <Col className="text-center p-5" style={{ color: "#6b7280", fontSize: 13 }}>
                                Loading variation family...
                            </Col>
                        </Row>
                    )}

                    {!loading && error && (
                        <Row className="m-0">
                            <Col className="p-0">
                                <div style={{
                                    background: "#fff3f3", border: "1px solid #ffb3b3",
                                    borderRadius: 8, padding: "10px 14px", color: "#cc0000", fontSize: 12,
                                }}>
                                    {error}
                                </div>
                            </Col>
                        </Row>
                    )}

                    {successMsg && (
                        <Row className="m-0 mb-2">
                            <Col className="p-0">
                                <div style={{
                                    background: "#f0fdf4", border: "1px solid #86efac",
                                    borderRadius: 8, padding: "8px 14px", color: "#166534", fontSize: 12,
                                }}>
                                    {successMsg}
                                </div>
                            </Col>
                        </Row>
                    )}

                    {!loading && !error && paginatedChildren.map(function (child) {
                        return (
                            <ChildCard
                                key={(child.sku || "nosku") + "__" + child.asin}
                                child={child}
                                fieldKey={fieldKey}
                                fieldLabel={fieldLabel}
                                fieldMeta={fieldMeta}
                                isImgField={isImgField}
                                editedValues={editedValues}
                                onEdit={handleEdit}
                                onImageEditClick={handleImageEditClick}
                            />
                        );
                    })}

                    {!loading && !error && filteredChildren.length === 0 && (
                        <Row className="m-0">
                            <Col className="text-center p-5" style={{ color: "#6b7280", fontSize: 13 }}>
                                {searchText.trim()
                                    ? "No results for \"" + searchText + "\""
                                    : "No variation family found for this product."}
                            </Col>
                        </Row>
                    )}
                </div>

                {/* Footer */}
                <Row className="m-0 d-flex justify-content-end align-items-center" style={{ paddingTop: 25, borderTop: "1px solid #e5e7eb" }}>
                    <Col xs="auto" className="p-0" style={{ display: "flex" }}>
                        <Button className="no-btn-background-color mr-3" onClick={handleClose}>Cancel</Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!hasEdits || submitting || hasBlankEdits}
                            className="btn-background-color"
                        >
                            {submitting ? "Saving…" : "Submit"}
                        </Button>
                    </Col>
                </Row>
                {showConfirm && (
                    <ConfirmUpdateModal
                        editedRows={editedRows.map(function (r) {
                            return {
                                asin: r.asin,
                                sku: r.sku,
                                editedAttrs: { [fieldKey]: Array.isArray(r.value) ? r.value.join(" | ") : r.value },
                            };
                        })}
                        varAttrs={[fieldKey]}
                        onConfirm={handleConfirmUpdate}
                        onCancel={function () { setShowConfirm(false); }}
                        submitting={submitting}
                    />
                )}

            </Card>

            {/* Image replace gallery popup — same pattern as AmazonProductListingGroupSection */}
            <CustomModal
                header={"Select Image"}
                isOpen={imagePopup.open}
                toggle={closeImagePopup}
                size={"xl"}
                className="curve-popup"
            >
                <AssetsgalleryImges
                    galleryImg={galleryImg}
                    setGalleryImg={setGalleryImg}
                    selectedImages={selectedAsset}
                    setSelectedImages={setSelectedAsset}
                    handleSubmitFile={handleImageReplace}
                    onUnselect={handleImageUnselect}
                    loading={galleryLoading}
                />
            </CustomModal>
        </>
    );
}

var mapStateToProps = function (state) { return { token: state.loggedUser.token }; };
export default connect(mapStateToProps)(VariationChildFieldPopup);